// Use something already written instead of writing it again.
//
// The other half of the seam (docs/AI_STUDIO_PLAN.md §2). MediaPicker's twin:
// same modal, same empty state, same escape key, different table. Compose asks
// for a kind and gets back a string — it never learns whether a model or a
// person wrote it, which is exactly the point of putting the boundary at
// `content_drafts` rather than at a generate button.
//
// Deliberately NOT gated on the AI plan. A workspace that pays for nothing and
// typed three captions by hand should still be able to pick one.

import { useEffect, useState } from "react";
import { deleteDraft, listDrafts } from "../lib/api";
import { Loading } from "./Spinner";
import type { ContentDraft, DraftKind, DraftSource } from "../lib/types";

/** Where it came from, in the words somebody would use about their own writing. */
const SOURCE_LABEL: Record<DraftSource, string> = {
  ai: "Written with AI",
  manual: "Written by hand",
  imported: "Imported from a file",
};

const KIND_LABEL: Record<DraftKind, string> = {
  caption: "captions",
  description: "descriptions",
  punchline: "punch lines",
  title: "titles",
};

export function DraftPicker({
  workspaceId, kind, onPick, onClose,
}: {
  workspaceId: string;
  /** What Compose is filling in. Omit to show everything. */
  kind?: DraftKind;
  onPick: (draft: ContentDraft) => void;
  onClose: () => void;
}) {
  const [drafts, setDrafts] = useState<ContentDraft[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Keyed rather than boolean: two rows removed in quick succession must not
  // put a spinner on both.
  const [removing, setRemoving] = useState<string | null>(null);

  useEffect(() => {
    setDrafts(null);
    listDrafts(workspaceId, kind)
      .then(setDrafts)
      .catch((e) => setError(e.message ?? String(e)));
  }, [workspaceId, kind]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function remove(draft: ContentDraft) {
    setRemoving(draft.id);
    setError(null);
    try {
      await deleteDraft(draft.id);
      setDrafts((rows) => (rows ?? []).filter((r) => r.id !== draft.id));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRemoving(null);
    }
  }

  const label = kind ? KIND_LABEL[kind] : "drafts";

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="draft-picker-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="storage-head">
          <h2 id="draft-picker-title">Use a saved draft</h2>
        </div>
        {error && <div className="err">{error}</div>}

        {!drafts ? (
          <Loading label="Loading your drafts…" />
        ) : drafts.length === 0 ? (
          <p className="muted">
            No saved {label} yet. Anything you write in the studio and save appears here.
          </p>
        ) : (
          <ul className="draft-list">
            {drafts.map((d) => (
              <li key={d.id}>
                <button className="draft-body" onClick={() => onPick(d)}>
                  <span className="draft-text">{d.body}</span>
                  <span className="muted draft-meta">
                    {SOURCE_LABEL[d.source] ?? "Saved"}
                    {" · "}
                    {new Date(d.created_at).toLocaleDateString()}
                  </span>
                </button>
                <button
                  className="danger draft-remove"
                  onClick={() => remove(d)}
                  disabled={removing === d.id}
                  aria-label="Delete this draft"
                >
                  {removing === d.id ? "Removing…" : "Delete"}
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="row modal-actions">
          <button onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
